import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MetricsStrip from "./components/MetricsStrip";
import SalesFunnel from "./components/SalesFunnel";
import RepLeaderboard from "./components/RepLeaderboard";
import PipelineTable from "./components/PipelineTable";
import QuotaChart from "./components/QuotaChart";
import VelocityMetrics from "./components/VelocityMetrics";
import MobileTabView from "./components/MobileTabView";
import { reportsApi, formatLastDataUtc } from "api/reports";
import { opportunitiesApi } from "api/deals";

const DEAL_SIZE_OPTIONS = [
  { value: "all", label: "All Sizes" },
  { value: "0_50l", label: "< ₹50L" },
  { value: "50l_1cr", label: "₹50L – ₹1Cr" },
  { value: "1cr_plus", label: "> ₹1Cr" },
];

const STAGE_ORDER = ["Prospecting", "Qualification", "Proposal", "Negotiation", "Closed Won", "Closed Lost"];

function getDealSizeBucket(amount) {
  if (amount < 5000000) return "0_50l";
  if (amount <= 10000000) return "50l_1cr";
  return "1cr_plus";
}

function stageToId(stageLabel = "") {
  return stageLabel.toLowerCase().replace(/\s+/g, "_");
}

function toCsvCell(value) {
  const text = String(value ?? "");
  if (text.includes(",") || text.includes("\n") || text.includes('"')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export default function SalesPerformance() {
  const [liveIndicator, setLiveIndicator] = useState(false);
  const [selectedRep, setSelectedRep] = useState("all");
  const [selectedDealSize, setSelectedDealSize] = useState("all");
  const [activeFunnelStage, setActiveFunnelStage] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const repOptions = useMemo(() => {
    const reps = widgets?.reps || [];
    return [{ value: "all", label: "All Reps" }, ...reps.map((rep) => ({ value: rep.value, label: rep.label }))];
  }, [widgets?.reps]);

  const loadData = async () => {
    try {
      const widgetsData = await reportsApi.getSales();
      setWidgets(widgetsData);
      if (widgetsData?.lastDataAtUtc) {
        setLiveIndicator(true);
      }
      setLoadError("");
    } catch (error) {
      setLoadError(error?.message || "Unable to load live sales performance data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const stageLabelToEnum = {
    Prospecting: "Prospecting",
    Qualification: "Qualification",
    Proposal: "Proposal",
    Negotiation: "Negotiation",
    "Closed Won": "ClosedWon",
    "Closed Lost": "ClosedLost",
  };

  const handleFunnelStageClick = (stage) => {
    setActiveFunnelStage(prev => prev === stage?.id ? null : stage?.id);
  };

  const handleUpdateStage = async (id, stageLabel) => {
    const stage = stageLabelToEnum?.[stageLabel] || "Prospecting";
    await opportunitiesApi.moveStage(id, stage);
    await loadData();
  };

  const filteredRows = useMemo(() => {
    const rows = widgets?.pipelineTable || [];
    return rows.filter((row) => {
      const repMatch = selectedRep === "all" || row?.rep === selectedRep;
      const dealSizeMatch = selectedDealSize === "all" || getDealSizeBucket(Number(row?.value || 0)) === selectedDealSize;
      return repMatch && dealSizeMatch;
    });
  }, [widgets?.pipelineTable, selectedRep, selectedDealSize]);

  const filteredFunnel = useMemo(() => {
    const grouped = STAGE_ORDER.map((stage) => {
      const rows = filteredRows.filter((row) => row?.stage === stage);
      return {
        stage,
        deals: rows.length,
        value: rows.reduce((sum, row) => sum + Number(row?.value || 0), 0),
      };
    });

    return grouped.map((item, index) => {
      const prevDeals = index === 0 ? grouped[0]?.deals : grouped[index - 1]?.deals;
      return {
        id: stageToId(item.stage),
        label: item.stage,
        deals: item.deals,
        value: item.value,
        conversionPercent: index === 0 || !prevDeals ? null : Number(((item.deals / prevDeals) * 100).toFixed(1)),
        color: widgets?.funnel?.find((f) => f?.label === item.stage)?.color || "#3B82F6",
      };
    });
  }, [filteredRows, widgets?.funnel]);

  const filteredLeaderboard = useMemo(() => {
    const rows = widgets?.leaderboard || [];
    const filtered = selectedRep === "all" ? rows : rows.filter((rep) => rep.name === selectedRep);
    return filtered.slice(0, 5);
  }, [widgets?.leaderboard, selectedRep]);

  const filteredMetrics = widgets?.metrics || [];

  const filteredQuotaTrend = widgets?.quotaTrend || [];

  const filteredVelocity = useMemo(() => {
    const kpis = (widgets?.velocity?.kpis || []).map((kpi, index) => ({
      ...kpi,
      color: ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6"][index % 4],
    }));
    return { kpis, rings: widgets?.velocity?.rings || [] };
  }, [widgets?.velocity]);

  const handleShareReport = () => {
    const rows = filteredRows;
    const headers = ["Company", "Stage", "Value", "Probability", "CloseDateUtc", "Rep", "Product"];
    const csv = [
      headers.join(","),
      ...rows.map((row) => [
        toCsvCell(row?.company),
        toCsvCell(row?.stage),
        toCsvCell(row?.value),
        toCsvCell(row?.probabilityPercent),
        toCsvCell(row?.closeDateUtc),
        toCsvCell(row?.rep),
        toCsvCell(row?.product),
      ].join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-performance-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
        <main className="px-4 md:px-6 lg:px-8 py-4 md:py-6 max-w-screen-2xl mx-auto">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-5">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl md:text-2xl font-heading font-bold" style={{ color: "var(--color-foreground)" }}>
                  Sales Performance
                </h1>
                {liveIndicator && (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-caption font-medium"
                    style={{ background: "#ECFDF5", color: "#059669" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live
                  </span>
                )}
              </div>
              <p className="text-sm font-caption mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                Pipeline analytics &amp; team performance · As of {formatLastDataUtc(widgets?.lastDataAtUtc)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" iconName="Share2" iconPosition="left" iconSize={14} onClick={handleShareReport}>
                Share Report
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={() => loadData()} disabled={isLoading}>
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Filters apply to the pipeline table below
            </span>
          </div>

          {/* Advanced Filters Row */}
          <div className="hidden md:flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-xs font-caption" style={{ color: "var(--color-text-secondary)" }}>
              <Icon name="SlidersHorizontal" size={13} color="currentColor" className="inline mr-1" />
              Filters:
            </span>
            <select
              value={selectedRep}
              onChange={(e) => setSelectedRep(e?.target?.value)}
              className="text-xs px-3 py-1.5 rounded-md border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", height: 32 }}
              aria-label="Filter by sales rep"
            >
              {repOptions?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
            </select>
            <select
              value={selectedDealSize}
              onChange={(e) => setSelectedDealSize(e?.target?.value)}
              className="text-xs px-3 py-1.5 rounded-md border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", height: 32 }}
              aria-label="Filter by deal size"
            >
              {DEAL_SIZE_OPTIONS?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
            </select>
            {(selectedRep !== "all" || selectedDealSize !== "all") && (
              <button
                onClick={() => { setSelectedRep("all"); setSelectedDealSize("all"); }}
                className="flex items-center gap-1 text-xs font-caption px-2 py-1 rounded-md hover:bg-muted transition-colors"
                style={{ color: "var(--color-text-secondary)" }}
              >
                <Icon name="RotateCcw" size={11} color="currentColor" />
                Clear
              </button>
            )}
          </div>

          {/* MOBILE VIEW */}
          <div className="md:hidden">
            <MobileTabView
              metrics={filteredMetrics}
              funnel={filteredFunnel}
              pipelineRows={filteredRows}
              leaderboard={filteredLeaderboard}
              onUpdateStage={handleUpdateStage}
              onExport={handleShareReport}
            />
          </div>

          {/* DESKTOP / TABLET VIEW */}
          <div className="hidden md:flex flex-col gap-4 md:gap-5">
            {isLoading && (
              <div className="rounded-lg border px-4 py-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading live sales performance data...</p>
              </div>
            )}
            {!isLoading && loadError && (
              <div className="rounded-lg border px-4 py-5" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
              </div>
            )}
            <MetricsStrip metrics={filteredMetrics} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
              <div className="lg:col-span-2">
                <SalesFunnel onStageClick={handleFunnelStageClick} stages={filteredFunnel} />
              </div>
              <div className="lg:col-span-1">
                <RepLeaderboard reps={filteredLeaderboard} />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
              <div className="lg:col-span-2">
                <QuotaChart data={filteredQuotaTrend} />
              </div>
              <div className="lg:col-span-1">
                <VelocityMetrics kpis={filteredVelocity.kpis} velocityData={filteredVelocity.rings} />
              </div>
            </div>
            <PipelineTable
              onExport={handleShareReport}
              stageFilter={activeFunnelStage}
              rows={filteredRows}
              onUpdateStage={handleUpdateStage}
            />
            {activeFunnelStage && (
              <div className="flex items-center gap-2 -mt-2">
                <span className="text-xs font-caption" style={{ color: "var(--color-text-secondary)" }}>
                  Filtering pipeline by funnel stage:
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium font-caption"
                  style={{ background: "var(--color-muted)", color: "var(--color-primary)" }}>
                  {activeFunnelStage?.replace("_", " ")}
                </span>
                <button
                  onClick={() => setActiveFunnelStage(null)}
                  className="text-xs font-caption hover:underline"
                  style={{ color: "var(--color-error)" }}
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="mt-8 pt-4 border-t flex flex-col sm:flex-row items-center justify-between gap-2"
            style={{ borderColor: "var(--color-border)" }}>
            <p className="text-xs font-caption" style={{ color: "var(--color-text-secondary)" }}>
              &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved.
            </p>
            <p className="text-xs font-caption" style={{ color: "var(--color-text-secondary)" }}>
              Live data — reads straight from the CRM · As of {formatLastDataUtc(widgets?.lastDataAtUtc)}
            </p>
          </footer>
        </main>
      </div>
    </AppLayout>
  );
}